import React from "react";
import { ModalCtx } from "./components/shared.jsx";
import { Modal, Nav, Footer } from "./components/shared-layout.jsx";
import { CookieConsent } from "./components/CookieConsent.jsx";

// High-traffic routes — eagerly imported (in main bundle).
import { HomePage } from "./components/HomePage.jsx";
import { UslugiPage, ONasPage } from "./components/Pages1.jsx";
import { KontaktPage } from "./components/KontaktPage.jsx";
import { PolitykaPrywatnosci, RegulaminPage, NotFoundPage } from "./components/LegalPages.jsx";

// Per-service subpages — lazy-loaded (Vite splits each into its own chunk +
// dedicated interactives-*.css). Reduces initial JS/CSS payload by ~40% for
// users who land on home and don't visit every service before bouncing.
const GoogleAdsPage = React.lazy(() => import("./components/GoogleAdsPage.jsx").then(m => ({ default: m.GoogleAdsPage })));
const MetaAdsPage = React.lazy(() => import("./components/MetaAdsPage.jsx").then(m => ({ default: m.MetaAdsPage })));
const AnalitykaPage = React.lazy(() => import("./components/AnalitykaPage.jsx").then(m => ({ default: m.AnalitykaPage })));
const CapiPage = React.lazy(() => import("./components/CapiPage.jsx").then(m => ({ default: m.CapiPage })));
const FeedPage = React.lazy(() => import("./components/FeedPage.jsx").then(m => ({ default: m.FeedPage })));
const MarketingPage = React.lazy(() => import("./components/MarketingPage.jsx").then(m => ({ default: m.MarketingPage })));
const AllegroPage = React.lazy(() => import("./components/AllegroPage.jsx").then(m => ({ default: m.AllegroPage })));
const CaseStudiesPage = React.lazy(() => import("./components/Cases.jsx").then(m => ({ default: m.CaseStudiesPage })));
const CaseDetailPage = React.lazy(() => import("./components/Cases.jsx").then(m => ({ default: m.CaseDetailPage })));

// Landing win-back — lazy, niepubliczny (noindex + robots.txt Disallow), NIE w prerenderze.
const Energetyczne24Page = React.lazy(() => import("./components/Energetyczne24Page.jsx").then(m => ({ default: m.Energetyczne24Page })));
const NowosciSierpien2026Page = React.lazy(() => import("./components/NowosciSierpien2026Page.jsx"));

// SPIKE: wizualny edytor (Puck) — lazy, NIE w prerenderze (scripts/prerender.mjs),
// BEZ linku w nawigacji. Puck + jego CSS trafiają wyłącznie do tego chunku.
const Editor = React.lazy(() => import("./puck/Editor.jsx"));

// Strip trailing slash so route comparisons work consistently regardless of how
// the browser landed (with/without slash). Root "/" stays as "/".
const normalize = (p) => (!p || p === "/") ? "/" : p.replace(/\/+$/, "");

const App = () => {
  const [route, setRoute] = React.useState(() => normalize(window.location.pathname));
  const [modalOpen, setModalOpen] = React.useState(false);
  const navigate = React.useCallback((r) => {
    const target = normalize(r);
    if (target !== window.location.pathname) {
      window.history.pushState({}, "", target);
    }
    setRoute(target);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  // Clean URL bar on initial load if user landed on /foo/ — replace with /foo.
  React.useEffect(() => {
    const path = window.location.pathname;
    const norm = normalize(path);
    if (path !== norm) {
      window.history.replaceState({}, "", norm + window.location.search + window.location.hash);
    }
  }, []);

  React.useEffect(() => {
    const onPop = () => setRoute(normalize(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const modalCtx = React.useMemo(() => ({
    open: () => setModalOpen(true),
    close: () => setModalOpen(false),
  }), []);

  // Redirect legacy standalone URLs to anchored sections on /o-nas
  React.useEffect(() => {
    if (route === "/dlaczego-my") {
      window.history.replaceState({}, "", "/o-nas#dlaczego-my");
      setRoute("/o-nas");
      setTimeout(() => document.getElementById("dlaczego-my")?.scrollIntoView({ behavior: "smooth" }), 200);
    } else if (route === "/technologia") {
      window.history.replaceState({}, "", "/o-nas#technologia");
      setRoute("/o-nas");
      setTimeout(() => document.getElementById("technologia")?.scrollIntoView({ behavior: "smooth" }), 200);
    }
  }, [route]);

  // Edytor wizualny pełnoekranowo, bez Nav/Footer/Modal (osobny tryb).
  // /admin → wybór strony; /admin/<page> → edytor danej strony.
  if (route === "/admin" || route.startsWith("/admin/")) {
    const page = route === "/admin" ? "" : route.slice("/admin/".length);
    return (
      <React.Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
        <Editor page={page} />
      </React.Suspense>
    );
  }

  // Landing win-back /energetyczne24 — goła powłoka (bez globalnego Nav/Footer, jak /admin),
  // ale WEWNĄTRZ ModalCtx + własny <Modal/> (App renderuje Modal dopiero po tym returnie) + RODO.
  if (route === "/energetyczne24") {
    return (
      <ModalCtx.Provider value={modalCtx}>
        <React.Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
          <Energetyczne24Page navigate={navigate} />
        </React.Suspense>
        {modalOpen && <Modal onClose={() => setModalOpen(false)} />}
        <CookieConsent />
      </ModalCtx.Provider>
    );
  }

  // Komunikat dla klientów /nowosci/… — jak /energetyczne24: goła powłoka, bo strona
  // ma własny nav kotwicowy i własną stopkę. noindex, poza sitemap i prerenderem.
  if (route === "/nowosci/sierpien-2026") {
    return (
      <ModalCtx.Provider value={modalCtx}>
        <React.Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
          <NowosciSierpien2026Page />
        </React.Suspense>
        {modalOpen && <Modal onClose={() => setModalOpen(false)} />}
        <CookieConsent />
      </ModalCtx.Provider>
    );
  }

  let Page = null;
  if (route === "/") Page = <HomePage navigate={navigate} />;
  else if (route === "/uslugi") Page = <UslugiPage navigate={navigate} />;
  else if (route === "/uslugi/google-ads") Page = <GoogleAdsPage navigate={navigate} />;
  else if (route === "/uslugi/meta-ads") Page = <MetaAdsPage navigate={navigate} />;
  else if (route === "/uslugi/analityka") Page = <AnalitykaPage navigate={navigate} />;
  else if (route === "/uslugi/capi") Page = <CapiPage navigate={navigate} />;
  else if (route === "/uslugi/feed") Page = <FeedPage navigate={navigate} />;
  else if (route === "/uslugi/marketing-dla-ecommerce") Page = <MarketingPage navigate={navigate} />;
  else if (route === "/uslugi/allegro") Page = <AllegroPage navigate={navigate} />;
  else if (route === "/case-studies") Page = <CaseStudiesPage navigate={navigate} />;
  else if (route.startsWith("/case-studies/")) Page = <CaseDetailPage navigate={navigate} slug={route.split("/").pop()} />;
  else if (route === "/o-nas" || route === "/podejscie" || route === "/dlaczego-my" || route === "/technologia") Page = <ONasPage navigate={navigate} />;
  else if (route === "/kontakt") Page = <KontaktPage navigate={navigate} />;
  else if (route === "/polityka-prywatnosci") Page = <PolitykaPrywatnosci navigate={navigate} />;
  else if (route === "/regulamin") Page = <RegulaminPage navigate={navigate} />;
  else Page = <NotFoundPage navigate={navigate} route={route} />;

  return (
    <ModalCtx.Provider value={modalCtx}>
      <Nav route={route} navigate={navigate} />
      <main key={route}>
        <React.Suspense fallback={<div style={{minHeight: "60vh"}} />}>{Page}</React.Suspense>
      </main>
      <Footer navigate={navigate} />
      {modalOpen && <Modal onClose={() => setModalOpen(false)} />}
      <CookieConsent />
    </ModalCtx.Provider>
  );
};

export default App;
